type ConsoleLevel = "error" | "warn" | "info" | "log";
type NetworkKind = "fetch" | "xhr";
type WebSocketDirection = "in" | "out";

export interface ConsoleCaptureEntry {
  t: number;
  level: ConsoleLevel;
  message: string;
}

export interface NetworkCaptureEntry {
  t: number;
  kind: NetworkKind;
  method: string;
  url: string;
  status?: number;
  ok: boolean;
  durationMs: number;
  error?: string;
}

export interface WebSocketCaptureEntry {
  t: number;
  direction: WebSocketDirection;
  kind: "json" | "text";
  url?: string;
  type?: string | null;
  family?: string | null;
  op?: string | null;
  bytesApprox: number;
  message: string;
}

export interface DomNodeSnapshot {
  tag: string;
  id?: string | null;
  classes?: string[];
  selector?: string;
  role?: string;
  label?: string | null;
  textPreview?: string | null;
  data?: Record<string, string>;
  attributes?: Record<string, string>;
  masked?: boolean;
  bounds?: { x: number; y: number; w: number; h: number };
  annotationRefs?: Array<{ id: string; index: number; title: string; note: string | null }>;
  truncatedChildren?: number;
  children?: DomNodeSnapshot[];
}

export interface ElementAncestorSnapshot {
  tag: string;
  id: string | null;
  role: string | null;
  classes: string[];
  selector: string;
  label: string | null;
  textPreview: string | null;
}

export interface ElementComponentHint {
  name: string;
  source: string;
  selector: string | null;
}

export interface ElementTableContext {
  tableLabel: string | null;
  rowIndex: number;
  columnIndex: number;
  columnHeader: string | null;
  cellText: string | null;
  rowText: string | null;
  headers: string[];
  row: Record<string, string>;
}

export interface ElementTargetSnapshot {
  tag: string;
  id: string | null;
  classes: string[];
  data: Record<string, string>;
  attributes: Record<string, string>;
  selector: string;
  debugSummary: string;
  panel: string | null;
  component: ElementComponentHint | null;
  labels: string[];
  table: ElementTableContext | null;
  ancestry: ElementAncestorSnapshot[];
  textPreview: string | null;
  bounds: { x: number; y: number; w: number; h: number };
  dom: DomNodeSnapshot | null;
}

export interface DomAnnotationSnapshot {
  id: string;
  index: number;
  title: string;
  note: string | null;
  selectedElement: ElementTargetSnapshot;
}

export interface VisualMarkupSnapshot {
  id: string;
  index: number;
  color: string;
  title: string;
  note: string | null;
  bounds: { x: number; y: number; w: number; h: number };
}

export interface VisualCaptureSnapshot {
  kind: "annotated-region" | "one-shot";
  note: string | null;
  panel: string | null;
  component: ElementComponentHint | null;
  bounds: { x: number; y: number; w: number; h: number };
  viewport: { width: number; height: number; devicePixelRatio: number; scrollX: number; scrollY: number };
  image: {
    mime: "image/jpeg";
    dataUrl: string;
    width: number;
    height: number;
    bytesApprox: number;
  };
  markups?: VisualMarkupSnapshot[];
  createdAt: string;
}

export interface BugReportActionSnapshot {
  kind: "click";
  button: number;
  buttons: number;
  x: number;
  y: number;
  modifiers: string[];
  targetSelector: string;
  targetSummary: string;
  startedAt: string;
  completedAt: string;
  waitMs: number;
}

export interface BugReportEvidenceCaptureWindow {
  kind: "action" | "telemetry";
  startedAt: string;
  stoppedAt: string;
  durationMs: number;
}

export interface BugReportActionRecordingResult {
  startedAt: number;
  stoppedAt: number;
  actions: BugReportActionSnapshot[];
  firstTarget: ElementTargetSnapshot | null;
}

export interface BugReportActionRecorder {
  startedAt: number;
  stop: () => BugReportActionRecordingResult;
}

interface VisualMarkupRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface VisualMarkupEditorResult {
  markups: VisualMarkupSnapshot[];
}

interface DomSnapshotOptions {
  annotationMap?: Map<string, Array<{ id: string; index: number; title: string; note: string | null }>>;
  elementAnnotationMap?: WeakMap<Element, Array<{ id: string; index: number; title: string; note: string | null }>>;
}

type VisualMarkupDragMode = "draw" | "resize";

export interface BugReportDraftSnapshot {
  draftId: string;
  startedAt: string;
  initialRoute: string;
  initialPanelId: string | null;
  initialContext: {
    route: string;
    panelId: string | null;
    sessionCode: string | null;
    hostId: string | null;
    runtime: BugReportContext["runtime"];
    ws?: Record<string, unknown> | null;
  };
  globalComment?: string | null;
  globalErrorMessage?: string | null;
}

export interface BugReportEvidenceContextDelta {
  route: string;
  panelId: string | null;
  component: ElementComponentHint | null;
  capture?: BugReportEvidenceCaptureWindow | null;
  actions: BugReportActionSnapshot[];
  console: ConsoleCaptureEntry[];
  network: NetworkCaptureEntry[];
  wsMessages: WebSocketCaptureEntry[];
  ws?: Record<string, unknown> | null;
  wsChanged: boolean;
  createdAt: string;
}

export interface BugReportEvidenceItem {
  id: string;
  index: number;
  title: string;
  annotation: string | null;
  action?: BugReportActionSnapshot | null;
  selectedElement?: ElementTargetSnapshot | null;
  domAnnotations?: DomAnnotationSnapshot[];
  domSnapshot?: DomNodeSnapshot | null;
  visual?: VisualCaptureSnapshot | null;
  contextDelta: BugReportEvidenceContextDelta;
  createdAt: string;
}

export interface BugReportEvidenceSummary {
  totalItems: number;
  finalOrder: Array<{ id: string; index: number; title: string; annotation: string | null }>;
  panels: string[];
  components: string[];
  hasDom: boolean;
  hasVisual: boolean;
  actionCount: number;
  consoleDeltaCount: number;
  networkDeltaCount: number;
  wsDeltaCount: number;
}

export interface BugReportContext {
  route: string;
  panelId: string | null;
  sessionCode: string | null;
  hostId: string | null;
  runtime: {
    path: string;
    url: string;
    userAgent: string;
    language: string;
    timezone: string;
    build: string | null;
    viewport: { width: number; height: number; devicePixelRatio: number };
  };
  console: ConsoleCaptureEntry[];
  network: NetworkCaptureEntry[];
  errorMessage?: string | null;
  latestError?: ConsoleCaptureEntry | null;
  ws?: Record<string, unknown>;
  dom?: DomNodeSnapshot | null;
  selectedElement?: ElementTargetSnapshot | null;
  visual?: VisualCaptureSnapshot | null;
  draft?: BugReportDraftSnapshot | null;
  evidenceItems?: BugReportEvidenceItem[];
  evidenceSummary?: BugReportEvidenceSummary;
}

const MAX_CONSOLE = 80;
const MAX_NETWORK = 80;
const MAX_WS_MESSAGES = 80;
const MAX_ACTIONS = 40;
const MAX_STRING = 1200;
const MAX_DOM_NODES = 240;
const MAX_SELECTED_DOM_NODES = 36;
const MAX_DOM_ANNOTATIONS = 12;
const MAX_VISUAL_DATA_URL = 120_000;
const MAX_EVIDENCE_CONSOLE = 30;
const MAX_EVIDENCE_NETWORK = 30;
const MAX_EVIDENCE_WS_MESSAGES = 30;
const VISUAL_MARKUP_COLORS = ["#facc15", "#38bdf8", "#fb7185", "#34d399", "#c084fc", "#f97316"];
const MESSAGE_DEDUPE_WINDOW_MS = 1500;
const SENSITIVE_KEY =
  /(password|passwd|pwd|secret|token|jwt|bearer|authorization|cookie|set-cookie|csrf|xsrf|api[-_]?key|credential|private[-_]?key|connection[-_]?string|session|otp|totp)/i;
const JWT_RE = /\beyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\b/g;
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+/=-]{16,}\b/gi;
const SECRET_ASSIGNMENT_RE =
  /\b(password|passwd|pwd|secret|token|api[-_]?key|authorization|cookie|jwt|connection[-_]?string)\b\s*[:=]\s*("[^"]+"|'[^']+'|[^\s,;]+)/gi;
const PRIVATE_SELECTOR = [
  "input",
  "textarea",
  "select",
  "[contenteditable='true']",
  "pre",
  "code",
  "canvas",
  "video",
  "[data-bug-private]",
  ".rdp-debug",
  ".video-stage",
  ".session-main",
].join(",");
const BUG_REPORT_UI_SELECTOR = [
  ".bug-report-overlay",
  ".bug-report-fab",
  ".bug-report-pick-highlight",
  ".bug-report-pick-label",
  ".bug-report-pick-instruction",
  ".bug-report-selected-highlight",
  ".bug-report-selected-label",
  ".bug-report-region-layer",
  ".bug-report-region-box",
  ".bug-report-region-label",
  ".bug-report-visual-annotator",
  ".bug-report-capture-editor",
  ".bug-report-recording-bar",
  ".bug-report-dom-annotation-bar",
  ".bug-report-dom-annotation-box",
  ".bug-report-dom-annotation-label",
].join(",");
const COMPONENT_HINT_SELECTOR = [
  "[data-component]",
  "[data-testid]",
  "[data-test]",
  "[aria-label]",
  "[role='dialog']",
  "[role='region']",
  "[role='complementary']",
  "form",
  "table",
  "section",
  "aside",
  "nav",
].join(",");
const SAFE_TARGET_ATTRIBUTES = [
  "role",
  "aria-label",
  "aria-current",
  "aria-expanded",
  "aria-selected",
  "title",
  "name",
  "type",
  "href",
  "data-testid",
  "data-test",
  "data-panel",
  "data-active",
  "data-host-id",
];

let installed = false;
const consoleBuffer: ConsoleCaptureEntry[] = [];
const networkBuffer: NetworkCaptureEntry[] = [];
const wsBuffer: WebSocketCaptureEntry[] = [];
let selectedHighlightCleanup: (() => void) | null = null;

export function installBugReportCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  patchConsole("error");
  patchConsole("warn");
  patchConsole("info");
  patchConsole("log");
  installWindowErrorCapture();
  patchFetch();
  patchXhr();
}

export function getBugReportBuffers() {
  return {
    console: consoleBuffer.slice(-MAX_CONSOLE),
    network: networkBuffer.slice(-MAX_NETWORK),
    wsMessages: wsBuffer.slice(-MAX_WS_MESSAGES),
  };
}

export function recordBugReportWsMessage(
  direction: WebSocketDirection,
  data: unknown,
  url?: string | URL | null,
): void {
  if (isBinaryWsData(data)) return;
  pushWsMessage(wsCaptureEntry(direction, data, url));
}

export function sanitizeText(value: unknown, max = MAX_STRING): string {
  const raw = stringifyArg(value)
    .replace(JWT_RE, "[REDACTED_JWT]")
    .replace(BEARER_RE, "Bearer [REDACTED]")
    .replace(SECRET_ASSIGNMENT_RE, (_m, key) => `${key}=[REDACTED]`);
  return raw.length > max ? `${raw.slice(0, max)}…` : raw;
}

export function sanitizeValue(value: unknown, depth = 0, key = ""): unknown {
  if (depth > 6) return "[TRUNCATED_DEPTH]";
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return SENSITIVE_KEY.test(key) ? "[REDACTED]" : sanitizeText(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.slice(0, 50).map((item) => sanitizeValue(item, depth + 1, key));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>).slice(0, 80)) {
      out[childKey] = SENSITIVE_KEY.test(childKey) ? "[REDACTED]" : sanitizeValue(childValue, depth + 1, childKey);
    }
    return out;
  }
  return sanitizeText(value);
}

export function captureBugReportContext(options: {
  includeDom?: boolean;
  wsState?: Record<string, unknown>;
  selectedElement?: ElementTargetSnapshot | null;
  visual?: VisualCaptureSnapshot | null;
  draft?: BugReportDraftSnapshot | null;
  evidenceItems?: BugReportEvidenceItem[];
  errorMessage?: string | null;
}): BugReportContext {
  const active = activePanel();
  const runtime = {
    path: window.location.pathname,
    url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    build: currentBundleHash(),
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
    },
  };
  const ws = options.wsState ? sanitizeWsState(options.wsState) : undefined;
  const evidenceItems = options.evidenceItems ? renumberEvidenceItems(options.evidenceItems) : undefined;
  const draft = options.draft
    ? {
        ...options.draft,
        globalComment: options.draft.globalComment ? sanitizeText(options.draft.globalComment, 2000) : null,
        globalErrorMessage: options.draft.globalErrorMessage
          ? sanitizeText(options.draft.globalErrorMessage, 2000)
          : null,
      }
    : null;
  return {
    route: runtime.path,
    panelId: active,
    sessionCode: typeof options.wsState?.sessionCode === "string" ? options.wsState.sessionCode : null,
    hostId: findCurrentHostId(),
    runtime,
    console: consoleBuffer.slice(-MAX_CONSOLE),
    network: networkBuffer.slice(-MAX_NETWORK),
    errorMessage: options.errorMessage ? sanitizeText(options.errorMessage, 2000) : null,
    latestError: latestConsoleError(),
    ...(ws ? { ws } : {}),
    ...(options.includeDom ? { dom: createDomSnapshot() } : {}),
    ...(options.selectedElement ? { selectedElement: options.selectedElement } : {}),
    ...(options.visual ? { visual: options.visual } : {}),
    ...(draft ? { draft } : {}),
    ...(evidenceItems ? { evidenceItems, evidenceSummary: buildBugReportEvidenceSummary(evidenceItems) } : {}),
  };
}

export function captureBugReportEvidenceDelta(options: {
  since: number;
  wsState?: Record<string, unknown>;
  previousWs?: Record<string, unknown> | null;
  selectedElement?: ElementTargetSnapshot | null;
  visual?: VisualCaptureSnapshot | null;
  actions?: BugReportActionSnapshot[];
  capture?: BugReportEvidenceCaptureWindow | null;
}): BugReportEvidenceContextDelta {
  const ws = options.wsState ? sanitizeWsState(options.wsState) : null;
  const component = options.selectedElement?.component || options.visual?.component || null;
  return {
    route: window.location.pathname,
    panelId: options.selectedElement?.panel || options.visual?.panel || activePanel(),
    component,
    capture: options.capture || null,
    actions: (options.actions || []).slice(-MAX_ACTIONS),
    console: consoleBuffer.filter((entry) => entry.t > options.since).slice(-MAX_EVIDENCE_CONSOLE),
    network: networkBuffer.filter((entry) => entry.t > options.since).slice(-MAX_EVIDENCE_NETWORK),
    wsMessages: wsBuffer.filter((entry) => entry.t > options.since).slice(-MAX_EVIDENCE_WS_MESSAGES),
    ws,
    wsChanged: stableJson(ws) !== stableJson(options.previousWs || null),
    createdAt: new Date().toISOString(),
  };
}

export function buildBugReportEvidenceSummary(items: BugReportEvidenceItem[]): BugReportEvidenceSummary {
  const panels = new Set<string>();
  const components = new Set<string>();
  let consoleDeltaCount = 0;
  let networkDeltaCount = 0;
  let wsDeltaCount = 0;
  for (const item of items) {
    const firstDomAnnotation = item.domAnnotations?.[0]?.selectedElement || null;
    const panel =
      item.selectedElement?.panel || firstDomAnnotation?.panel || item.visual?.panel || item.contextDelta.panelId;
    if (panel) panels.add(panel);
    const component =
      item.selectedElement?.component?.name ||
      firstDomAnnotation?.component?.name ||
      item.visual?.component?.name ||
      item.contextDelta.component?.name;
    if (component) components.add(component);
    consoleDeltaCount += item.contextDelta.console.length;
    networkDeltaCount += item.contextDelta.network.length;
    if (item.contextDelta.wsMessages.length) wsDeltaCount += item.contextDelta.wsMessages.length;
    else if (item.contextDelta.wsChanged || item.contextDelta.ws) wsDeltaCount += 1;
  }
  return {
    totalItems: items.length,
    finalOrder: items.map((item, index) => ({
      id: item.id,
      index: index + 1,
      title: item.title,
      annotation: item.annotation || null,
    })),
    panels: Array.from(panels).slice(0, 12),
    components: Array.from(components).slice(0, 12),
    hasDom: items.some((item) => Boolean(item.selectedElement || item.domAnnotations?.length || item.domSnapshot)),
    hasVisual: items.some((item) => Boolean(item.visual)),
    actionCount: items.reduce((count, item) => {
      const actionCount = item.contextDelta.actions?.length || (item.action ? 1 : 0);
      return count + actionCount;
    }, 0),
    consoleDeltaCount,
    networkDeltaCount,
    wsDeltaCount,
  };
}

function renumberEvidenceItems(items: BugReportEvidenceItem[]): BugReportEvidenceItem[] {
  return items.map((item, index) => ({
    ...item,
    index: index + 1,
    annotation: item.annotation ? sanitizeText(item.annotation, 1200) : null,
    title: sanitizeText(item.title || `Capture ${index + 1}`, 220),
  }));
}

export function captureElementTarget(el: Element): ElementTargetSnapshot {
  const htmlEl = el as HTMLElement;
  const rect = htmlEl.getBoundingClientRect();
  const masked = htmlEl.matches(PRIVATE_SELECTOR) || Boolean(htmlEl.closest(PRIVATE_SELECTOR));
  const panel = activePanel();
  const table = targetTableContext(htmlEl);
  const component = inferComponent(htmlEl, panel);
  const textPreview = masked ? null : sanitizeText((htmlEl.textContent || "").replace(/\s+/g, " ").trim(), 280) || null;
  const labels = targetLabels(htmlEl, table, component);
  return {
    tag: htmlEl.tagName.toLowerCase(),
    id: htmlEl.id ? sanitizeText(htmlEl.id, 160) : null,
    classes: Array.from(htmlEl.classList)
      .filter((name) => !name.startsWith("bug-report-"))
      .slice(0, 12)
      .map((name) => sanitizeText(name, 80)),
    data: targetDataset(htmlEl),
    attributes: targetAttributes(htmlEl),
    selector: buildElementSelector(htmlEl),
    debugSummary: targetDebugSummary(htmlEl, panel, component, table, textPreview),
    panel,
    component,
    labels,
    table,
    ancestry: targetAncestry(htmlEl),
    textPreview,
    bounds: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      w: Math.round(rect.width),
      h: Math.round(rect.height),
    },
    dom: createDomSnapshotFrom(htmlEl, MAX_SELECTED_DOM_NODES, 3),
  };
}

export function selectBugReportElement(): Promise<ElementTargetSnapshot | null> {
  if (typeof document === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    let current: Element | null = null;
    let done = false;
    const frame = document.createElement("div");
    const label = document.createElement("div");
    const instruction = document.createElement("div");
    frame.className = "bug-report-pick-highlight";
    label.className = "bug-report-pick-label";
    instruction.className = "bug-report-pick-instruction";
    instruction.textContent = "Cliquer l'élément à signaler · Échap annule";
    document.body.append(frame, label, instruction);
    document.documentElement.classList.add("bug-report-picking");

    const cleanup = () => {
      window.removeEventListener("mousemove", onMove, true);
      window.removeEventListener("mouseover", onMove, true);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      frame.remove();
      label.remove();
      instruction.remove();
      document.documentElement.classList.remove("bug-report-picking");
    };

    const finish = (target: Element | null) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(target ? captureElementTarget(target) : null);
    };

    function onMove(event: MouseEvent) {
      const target = pickElementAt(event.clientX, event.clientY);
      updatePickFrame(target, frame, label);
      current = target;
    }

    function onClick(event: MouseEvent) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      finish(pickElementAt(event.clientX, event.clientY) || current);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      finish(null);
    }

    function onViewportChange() {
      updatePickFrame(current, frame, label);
    }

    window.addEventListener("mousemove", onMove, true);
    window.addEventListener("mouseover", onMove, true);
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);

    window.setTimeout(() => {
      const target = pickElementAt(Math.round(window.innerWidth / 2), Math.round(window.innerHeight / 2));
      updatePickFrame(target, frame, label);
      current = target;
    }, 0);
  });
}

export function captureBugReportDomSnapshot(annotations: DomAnnotationSnapshot[] = []): DomNodeSnapshot | null {
  if (typeof document === "undefined") return null;
  return createDomSnapshot({ annotations });
}

export function selectBugReportDomAnnotations(maxItems = MAX_DOM_ANNOTATIONS): Promise<DomAnnotationSnapshot[] | null> {
  if (typeof document === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    let current: Element | null = null;
    let done = false;
    const annotations: DomAnnotationSnapshot[] = [];
    const frame = document.createElement("div");
    const label = document.createElement("div");
    const instruction = document.createElement("div");
    const bar = document.createElement("div");
    const count = document.createElement("strong");
    const validate = document.createElement("button");
    const clear = document.createElement("button");
    const cancel = document.createElement("button");
    const boxes: HTMLElement[] = [];

    frame.className = "bug-report-pick-highlight";
    label.className = "bug-report-pick-label";
    instruction.className = "bug-report-pick-instruction";
    instruction.textContent = "Cliquer plusieurs éléments DOM · Entrée valide · Échap annule";
    bar.className = "bug-report-dom-annotation-bar";
    validate.type = "button";
    clear.type = "button";
    cancel.type = "button";
    validate.textContent = "Valider";
    clear.textContent = "Effacer";
    cancel.textContent = "Annuler";
    bar.append(count, validate, clear, cancel);
    document.body.append(frame, label, instruction, bar);
    document.documentElement.classList.add("bug-report-picking");

    const cleanup = () => {
      window.removeEventListener("mousemove", onMove, true);
      window.removeEventListener("mouseover", onMove, true);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      validate.removeEventListener("click", onValidate);
      clear.removeEventListener("click", onClear);
      cancel.removeEventListener("click", onCancel);
      frame.remove();
      label.remove();
      instruction.remove();
      bar.remove();
      boxes.forEach((box) => box.remove());
      document.documentElement.classList.remove("bug-report-picking");
    };

    const finish = (value: DomAnnotationSnapshot[] | null) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(value);
    };

    function updateBar() {
      count.textContent = `${annotations.length}/${maxItems} éléments DOM`;
      validate.disabled = annotations.length === 0;
      clear.disabled = annotations.length === 0;
    }

    function renderBoxes() {
      boxes.forEach((box) => box.remove());
      boxes.length = 0;
      for (const annotation of annotations) {
        const rect = annotation.selectedElement.bounds;
        const box = document.createElement("div");
        const boxLabel = document.createElement("div");
        box.className = "bug-report-dom-annotation-box";
        boxLabel.className = "bug-report-dom-annotation-label";
        boxLabel.textContent = `#${annotation.index}`;
        Object.assign(box.style, {
          left: `${rect.x}px`,
          top: `${rect.y}px`,
          width: `${Math.max(1, rect.w)}px`,
          height: `${Math.max(1, rect.h)}px`,
          display: "block",
        });
        Object.assign(boxLabel.style, {
          left: `${Math.max(6, rect.x)}px`,
          top: `${Math.max(6, rect.y - 28)}px`,
        });
        document.body.append(box, boxLabel);
        boxes.push(box, boxLabel);
      }
    }

    function addAnnotation(target: Element) {
      if (annotations.length >= maxItems) return;
      const selectedElement = captureElementTarget(target);
      const existingIndex = annotations.findIndex((item) => item.selectedElement.selector === selectedElement.selector);
      const existing = existingIndex >= 0 ? annotations[existingIndex] : null;
      const note = window.prompt("Note pour cet élément DOM", existing?.note || "");
      if (note === null && !existing) return;
      const next: DomAnnotationSnapshot = {
        id: existing?.id || `dom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        index: existing?.index || annotations.length + 1,
        title: selectedElement.debugSummary || selectedElement.selector,
        note: sanitizeText(note || "", 800).trim() || null,
        selectedElement,
      };
      if (existingIndex >= 0) annotations[existingIndex] = next;
      else annotations.push(next);
      annotations.forEach((item, index) => {
        item.index = index + 1;
      });
      updateBar();
      renderBoxes();
    }

    function onMove(event: MouseEvent) {
      const target = pickElementAt(event.clientX, event.clientY);
      updatePickFrame(target, frame, label);
      current = target;
    }

    function onClick(event: MouseEvent) {
      const source = event.target;
      if (
        source instanceof Element &&
        (source.matches(BUG_REPORT_UI_SELECTOR) || source.closest(BUG_REPORT_UI_SELECTOR))
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const target = pickElementAt(event.clientX, event.clientY) || current;
      if (target) addAnnotation(target);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        finish(null);
      } else if (event.key === "Enter" && annotations.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        finish(annotations.slice());
      }
    }

    function onViewportChange() {
      updatePickFrame(current, frame, label);
      renderBoxes();
    }

    function onValidate() {
      finish(annotations.length ? annotations.slice() : null);
    }

    function onClear() {
      annotations.splice(0, annotations.length);
      updateBar();
      renderBoxes();
    }

    function onCancel() {
      finish(null);
    }

    updateBar();
    validate.addEventListener("click", onValidate);
    clear.addEventListener("click", onClear);
    cancel.addEventListener("click", onCancel);
    window.addEventListener("mousemove", onMove, true);
    window.addEventListener("mouseover", onMove, true);
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);

    window.setTimeout(() => {
      const target = pickElementAt(Math.round(window.innerWidth / 2), Math.round(window.innerHeight / 2));
      updatePickFrame(target, frame, label);
      current = target;
    }, 0);
  });
}

export function selectBugReportActionClick(waitMs = 1200): Promise<{
  target: ElementTargetSnapshot;
  action: BugReportActionSnapshot;
} | null> {
  if (typeof document === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    let current: Element | null = null;
    let done = false;
    const frame = document.createElement("div");
    const label = document.createElement("div");
    const instruction = document.createElement("div");
    frame.className = "bug-report-pick-highlight";
    label.className = "bug-report-pick-label";
    instruction.className = "bug-report-pick-instruction";
    instruction.textContent = "Cliquer l'action à capturer · Échap annule";
    document.body.append(frame, label, instruction);
    document.documentElement.classList.add("bug-report-picking");

    const cleanup = () => {
      window.removeEventListener("mousemove", onMove, true);
      window.removeEventListener("mouseover", onMove, true);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      frame.remove();
      label.remove();
      instruction.remove();
      document.documentElement.classList.remove("bug-report-picking");
    };

    const cancel = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve(null);
    };

    function onMove(event: MouseEvent) {
      const target = pickElementAt(event.clientX, event.clientY);
      updatePickFrame(target, frame, label);
      current = target;
    }

    function onClick(event: MouseEvent) {
      if (done) return;
      const target = pickElementAt(event.clientX, event.clientY) || current;
      if (!target) return;
      done = true;
      const startedAt = new Date().toISOString();
      const targetSnapshot = captureElementTarget(target);
      const action: BugReportActionSnapshot = {
        kind: "click",
        button: event.button,
        buttons: event.buttons,
        x: Math.round(event.clientX),
        y: Math.round(event.clientY),
        modifiers: clickModifiers(event),
        targetSelector: targetSnapshot.selector,
        targetSummary: targetSnapshot.debugSummary || targetSnapshot.selector,
        startedAt,
        completedAt: startedAt,
        waitMs,
      };
      cleanup();
      window.setTimeout(() => {
        resolve({
          target: targetSnapshot,
          action: {
            ...action,
            completedAt: new Date().toISOString(),
          },
        });
      }, waitMs);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      cancel();
    }

    function onViewportChange() {
      updatePickFrame(current, frame, label);
    }

    window.addEventListener("mousemove", onMove, true);
    window.addEventListener("mouseover", onMove, true);
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);

    window.setTimeout(() => {
      const target = pickElementAt(Math.round(window.innerWidth / 2), Math.round(window.innerHeight / 2));
      updatePickFrame(target, frame, label);
      current = target;
    }, 0);
  });
}

export function startBugReportActionRecording(): BugReportActionRecorder {
  const startedAt = Date.now();
  const actions: BugReportActionSnapshot[] = [];
  let firstTarget: ElementTargetSnapshot | null = null;
  let stopped: BugReportActionRecordingResult | null = null;

  function onClick(event: MouseEvent) {
    const source = event.target;
    if (
      source instanceof Element &&
      (source.matches(BUG_REPORT_UI_SELECTOR) || source.closest(BUG_REPORT_UI_SELECTOR))
    ) {
      return;
    }
    const target = pickElementAt(event.clientX, event.clientY);
    if (!target) return;
    const targetSnapshot = captureElementTarget(target);
    if (!firstTarget) firstTarget = targetSnapshot;
    const at = new Date(event.timeStamp > 0 ? performance.timeOrigin + event.timeStamp : Date.now()).toISOString();
    actions.push({
      kind: "click",
      button: event.button,
      buttons: event.buttons,
      x: Math.round(event.clientX),
      y: Math.round(event.clientY),
      modifiers: clickModifiers(event),
      targetSelector: targetSnapshot.selector,
      targetSummary: targetSnapshot.debugSummary || targetSnapshot.selector,
      startedAt: at,
      completedAt: at,
      waitMs: 0,
    });
    if (actions.length > MAX_ACTIONS) actions.splice(0, actions.length - MAX_ACTIONS);
  }

  document.addEventListener("click", onClick, true);

  return {
    startedAt,
    stop: () => {
      if (stopped) return stopped;
      document.removeEventListener("click", onClick, true);
      stopped = {
        startedAt,
        stoppedAt: Date.now(),
        actions: actions.slice(),
        firstTarget,
      };
      return stopped;
    },
  };
}

export function highlightBugReportElement(target: ElementTargetSnapshot, durationMs = 2200): void {
  clearBugReportElementHighlight();
  if (typeof document === "undefined") return;
  const frame = document.createElement("div");
  const label = document.createElement("div");
  frame.className = "bug-report-selected-highlight";
  label.className = "bug-report-selected-label";
  label.textContent = target.id ? `${target.tag}#${target.id}` : target.selector;
  document.body.append(frame, label);

  const place = () => {
    const el = safeQuerySelector(target.selector);
    const rect = el?.getBoundingClientRect();
    placeFrame(
      frame,
      label,
      rect || { x: target.bounds.x, y: target.bounds.y, width: target.bounds.w, height: target.bounds.h },
      label.textContent || target.tag,
    );
  };
  const cleanup = () => {
    window.removeEventListener("scroll", place, true);
    window.removeEventListener("resize", place, true);
    frame.remove();
    label.remove();
    if (selectedHighlightCleanup === cleanup) selectedHighlightCleanup = null;
  };
  selectedHighlightCleanup = cleanup;
  window.addEventListener("scroll", place, true);
  window.addEventListener("resize", place, true);
  place();
  if (durationMs > 0) window.setTimeout(cleanup, durationMs);
}

export function clearBugReportElementHighlight(): void {
  selectedHighlightCleanup?.();
}

export async function captureBugReportVisualRegion(): Promise<VisualCaptureSnapshot | null> {
  if (typeof document === "undefined") return null;
  const bounds = await selectViewportRegion();
  if (!bounds) return null;
  const note = await requestVisualAnnotation(bounds);
  if (note === null) return null;
  await nextPaint();
  return buildVisualCaptureSnapshot("annotated-region", bounds, note);
}

export async function captureBugReportVisualOneShot(): Promise<VisualCaptureSnapshot | null> {
  if (typeof document === "undefined") return null;
  await nextPaint();
  return buildVisualCaptureSnapshot("one-shot", {
    x: 0,
    y: 0,
    w: Math.round(window.innerWidth),
    h: Math.round(window.innerHeight),
  });
}

async function buildVisualCaptureSnapshot(
  kind: VisualCaptureSnapshot["kind"],
  bounds: { x: number; y: number; w: number; h: number },
  note = "",
): Promise<VisualCaptureSnapshot | null> {
  const rendered = await renderRegionImage(bounds, note);
  if (!rendered) return null;
  const { image, markups } = rendered;
  const probeX = Math.max(0, Math.min(window.innerWidth - 1, bounds.x + Math.round(bounds.w / 2)));
  const probeY = Math.max(0, Math.min(window.innerHeight - 1, bounds.y + Math.round(bounds.h / 2)));
  const panel = activePanel();
  return {
    kind,
    note: note ? sanitizeText(note, 1200) : null,
    panel,
    component: inferComponent((document.elementFromPoint(probeX, probeY) || document.body) as HTMLElement, panel),
    bounds,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
      scrollX: Math.round(window.scrollX),
      scrollY: Math.round(window.scrollY),
    },
    image,
    markups,
    createdAt: new Date().toISOString(),
  };
}

export async function editBugReportVisualCapture(visual: VisualCaptureSnapshot): Promise<VisualCaptureSnapshot | null> {
  const canvas = await imageDataUrlToCanvas(visual.image.dataUrl);
  if (!canvas) return null;
  const result = await editVisualCaptureCanvas(canvas, visual.markups || []);
  if (!result) return null;
  return {
    ...visual,
    markups: result.markups,
  };
}

function selectViewportRegion(): Promise<{ x: number; y: number; w: number; h: number } | null> {
  return new Promise((resolve) => {
    let start: { x: number; y: number } | null = null;
    let current: { x: number; y: number } | null = null;
    let done = false;
    const layer = document.createElement("div");
    const box = document.createElement("div");
    const label = document.createElement("div");
    layer.className = "bug-report-region-layer";
    box.className = "bug-report-region-box";
    label.className = "bug-report-region-label";
    label.textContent = "Glisser pour capturer une zone · Échap annule";
    document.body.append(layer, box, label);
    document.documentElement.classList.add("bug-report-picking");

    const cleanup = () => {
      layer.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("mousemove", onMouseMove, true);
      window.removeEventListener("mouseup", onMouseUp, true);
      document.removeEventListener("keydown", onKeyDown, true);
      layer.remove();
      box.remove();
      label.remove();
      document.documentElement.classList.remove("bug-report-picking");
    };
    const finish = (bounds: { x: number; y: number; w: number; h: number } | null) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(bounds);
    };

    function onMouseDown(event: MouseEvent) {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      start = clampPoint(event.clientX, event.clientY);
      current = start;
      updateRegionBox(box, start, current);
    }

    function onMouseMove(event: MouseEvent) {
      if (!start) return;
      event.preventDefault();
      event.stopPropagation();
      current = clampPoint(event.clientX, event.clientY);
      updateRegionBox(box, start, current);
    }

    function onMouseUp(event: MouseEvent) {
      if (!start || !current) return;
      event.preventDefault();
      event.stopPropagation();
      const bounds = normalizedBounds(start, current);
      finish(bounds.w >= 12 && bounds.h >= 12 ? bounds : null);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      finish(null);
    }

    layer.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("mousemove", onMouseMove, true);
    window.addEventListener("mouseup", onMouseUp, true);
    document.addEventListener("keydown", onKeyDown, true);
  });
}

function requestVisualAnnotation(bounds: { x: number; y: number; w: number; h: number }): Promise<string | null> {
  return new Promise((resolve) => {
    let done = false;
    const panel = document.createElement("div");
    const title = document.createElement("div");
    const textarea = document.createElement("textarea");
    const actions = document.createElement("div");
    const cancel = document.createElement("button");
    const confirm = document.createElement("button");
    panel.className = "bug-report-visual-annotator";
    title.className = "bug-report-visual-annotator-title";
    title.textContent = "Annotation visuelle";
    textarea.placeholder = "Expliquer ce qu'il faut regarder dans cette zone";
    textarea.rows = 3;
    cancel.type = "button";
    cancel.textContent = "Annuler";
    confirm.type = "button";
    confirm.textContent = "Valider";
    actions.append(cancel, confirm);
    panel.append(title, textarea, actions);
    document.body.append(panel);
    const left = Math.min(Math.max(8, bounds.x), Math.max(8, window.innerWidth - 328));
    const below = bounds.y + bounds.h + 8;
    const top = below + 128 < window.innerHeight ? below : Math.max(8, bounds.y - 138);
    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;

    const cleanup = () => {
      cancel.removeEventListener("click", onCancel);
      confirm.removeEventListener("click", onConfirm);
      textarea.removeEventListener("keydown", onKeyDown);
      panel.remove();
    };
    const finish = (value: string | null) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(value);
    };
    function onCancel() {
      finish(null);
    }
    function onConfirm() {
      finish(sanitizeText(textarea.value, 1200).trim());
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(null);
      }
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        onConfirm();
      }
    }
    cancel.addEventListener("click", onCancel);
    confirm.addEventListener("click", onConfirm);
    textarea.addEventListener("keydown", onKeyDown);
    textarea.focus();
  });
}

async function renderRegionImage(
  bounds: { x: number; y: number; w: number; h: number },
  note: string,
): Promise<{ image: VisualCaptureSnapshot["image"]; markups: VisualMarkupSnapshot[] } | null> {
  const { default: html2canvas } = await import("html2canvas");
  const scale = Math.min(1.5, Math.max(1, window.devicePixelRatio || 1));
  const canvas = await html2canvas(document.body, {
    allowTaint: false,
    backgroundColor: getComputedStyle(document.body).backgroundColor || null,
    height: window.innerHeight,
    ignoreElements: (el) =>
      el instanceof Element &&
      (el.matches(BUG_REPORT_UI_SELECTOR) ||
        Boolean(el.closest(BUG_REPORT_UI_SELECTOR)) ||
        el.classList.contains("bug-report-fab")),
    logging: false,
    scale,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    useCORS: true,
    width: window.innerWidth,
    windowHeight: window.innerHeight,
    windowWidth: window.innerWidth,
    x: window.scrollX,
    y: window.scrollY,
  });
  const scaleX = canvas.width / window.innerWidth;
  const scaleY = canvas.height / window.innerHeight;
  const sx = Math.max(0, Math.round(bounds.x * scaleX));
  const sy = Math.max(0, Math.round(bounds.y * scaleY));
  const sw = Math.min(canvas.width - sx, Math.round(bounds.w * scaleX));
  const sh = Math.min(canvas.height - sy, Math.round(bounds.h * scaleY));
  const fit = Math.min(1, 920 / Math.max(1, sw), 680 / Math.max(1, sh));
  const noteHeight = note ? 44 : 0;
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(sw * fit));
  out.height = Math.max(1, Math.round(sh * fit) + noteHeight);
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(0, 0, out.width, out.height);
  if (note) {
    ctx.fillStyle = "#16213a";
    ctx.fillRect(0, 0, out.width, noteHeight);
    ctx.fillStyle = "#f5c451";
    ctx.font = "700 13px sans-serif";
    ctx.fillText("Annotation", 12, 17);
    ctx.fillStyle = "#f8fafc";
    ctx.font = "12px sans-serif";
    ctx.fillText(sanitizeText(note, 150), 12, 34);
  }
  ctx.drawImage(canvas, sx, sy, sw, sh, 0, noteHeight, out.width, out.height - noteHeight);
  ctx.strokeStyle = "#5ba8ff";
  ctx.lineWidth = 2;
  ctx.strokeRect(1, noteHeight + 1, out.width - 2, out.height - noteHeight - 2);
  const edited = await editVisualCaptureCanvas(out, []);
  if (!edited) return null;
  const dataUrl = encodeCanvas(out);
  return {
    image: {
      mime: "image/jpeg",
      dataUrl,
      width: out.width,
      height: out.height,
      bytesApprox: Math.round((dataUrl.length * 3) / 4),
    },
    markups: edited.markups,
  };
}

function editVisualCaptureCanvas(
  source: HTMLCanvasElement,
  initialMarkups: VisualMarkupSnapshot[],
): Promise<VisualMarkupEditorResult | null> {
  return new Promise((resolve) => {
    let done = false;
    let selectedIndex = initialMarkups.length ? 0 : -1;
    let active: VisualMarkupRect | null = null;
    let dragMode: VisualMarkupDragMode | null = null;
    let resizeIndex = -1;
    const markups: VisualMarkupSnapshot[] = initialMarkups.map((markup, index) =>
      normalizeMarkupSnapshot(markup, index, source.width, source.height),
    );
    const overlay = document.createElement("div");
    const shell = document.createElement("div");
    const toolbar = document.createElement("div");
    const body = document.createElement("div");
    const stage = document.createElement("div");
    const title = document.createElement("strong");
    const spacer = document.createElement("span");
    const undo = document.createElement("button");
    const clear = document.createElement("button");
    const skip = document.createElement("button");
    const cancel = document.createElement("button");
    const confirm = document.createElement("button");
    const list = document.createElement("div");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      resolve({ markups });
      return;
    }

    overlay.className = "bug-report-capture-editor";
    shell.className = "bug-report-capture-editor-shell";
    toolbar.className = "bug-report-capture-editor-toolbar";
    body.className = "bug-report-capture-editor-body";
    stage.className = "bug-report-capture-editor-stage";
    list.className = "bug-report-capture-editor-list";
    canvas.className = "bug-report-capture-editor-canvas";
    title.textContent = "Annoter capture";
    undo.type = "button";
    undo.textContent = "Annuler cadre";
    clear.type = "button";
    clear.textContent = "Effacer";
    skip.type = "button";
    skip.textContent = "Sans cadre";
    cancel.type = "button";
    cancel.textContent = "Annuler";
    confirm.type = "button";
    confirm.textContent = "Valider";
    canvas.width = source.width;
    canvas.height = source.height;
    canvas.style.aspectRatio = `${source.width} / ${source.height}`;
    toolbar.append(title, spacer, undo, clear, skip, cancel, confirm);
    stage.append(canvas);
    body.append(stage, list);
    shell.append(toolbar, body);
    overlay.append(shell);
    document.body.append(overlay);

    function redraw() {
      ctx!.clearRect(0, 0, canvas.width, canvas.height);
      ctx!.drawImage(source, 0, 0);
      for (let index = 0; index < markups.length; index += 1) {
        drawMarkupRect(ctx!, markups[index], index === selectedIndex);
      }
      if (active) drawMarkupRect(ctx!, active, true);
      undo.disabled = markups.length === 0;
      clear.disabled = markups.length === 0;
    }

    function renderMarkupList() {
      list.replaceChildren();
      if (!markups.length) {
        const empty = document.createElement("div");
        empty.className = "bug-report-capture-editor-empty";
        empty.textContent = "Aucun cadre";
        list.append(empty);
        redraw();
        return;
      }
      for (let index = 0; index < markups.length; index += 1) {
        const markup = markups[index];
        const row = document.createElement("div");
        const swatch = document.createElement("span");
        const fields = document.createElement("div");
        const input = document.createElement("input");
        const textarea = document.createElement("textarea");
        const remove = document.createElement("button");
        row.className = "bug-report-capture-editor-row";
        row.dataset.active = index === selectedIndex ? "true" : "false";
        swatch.className = "bug-report-capture-editor-swatch";
        swatch.style.background = markup.color;
        fields.className = "bug-report-capture-editor-fields";
        input.value = markup.title;
        input.maxLength = 80;
        textarea.value = markup.note || "";
        textarea.rows = 2;
        textarea.maxLength = 240;
        remove.type = "button";
        remove.textContent = "Supprimer";
        row.addEventListener("click", () => {
          selectedIndex = index;
          renderMarkupList();
        });
        input.addEventListener("input", () => {
          markup.title = sanitizeText(input.value, 80) || `Cadre ${index + 1}`;
          redraw();
        });
        textarea.addEventListener("input", () => {
          markup.note = sanitizeText(textarea.value, 240) || null;
        });
        remove.addEventListener("click", (event) => {
          event.stopPropagation();
          markups.splice(index, 1);
          reindexMarkups(markups);
          selectedIndex = Math.min(markups.length - 1, index);
          renderMarkupList();
        });
        fields.append(input, textarea);
        row.append(swatch, fields, remove);
        list.append(row);
      }
      redraw();
    }

    function finish(value: VisualMarkupEditorResult | null) {
      if (done) return;
      done = true;
      cleanup();
      resolve(value);
    }

    function cleanup() {
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      undo.removeEventListener("click", onUndo);
      clear.removeEventListener("click", onClear);
      skip.removeEventListener("click", onSkip);
      cancel.removeEventListener("click", onCancel);
      confirm.removeEventListener("click", onConfirm);
      document.removeEventListener("keydown", onKeyDown, true);
      overlay.remove();
    }

    function onPointerDown(event: PointerEvent) {
      if (event.button !== 0) return;
      event.preventDefault();
      const point = canvasPoint(canvas, event);
      const resizeHit = hitTestResizeHandle(markups, point);
      if (resizeHit >= 0) {
        selectedIndex = resizeHit;
        resizeIndex = resizeHit;
        dragMode = "resize";
        renderMarkupList();
        canvas.setPointerCapture?.(event.pointerId);
        return;
      }
      const selected = hitTestMarkup(markups, point);
      if (selected >= 0) {
        selectedIndex = selected;
        renderMarkupList();
        return;
      }
      active = { x: point.x, y: point.y, w: 0, h: 0 };
      dragMode = "draw";
      canvas.setPointerCapture?.(event.pointerId);
      redraw();
    }

    function onPointerMove(event: PointerEvent) {
      if (!dragMode) return;
      event.preventDefault();
      const point = canvasPoint(canvas, event);
      if (dragMode === "resize" && resizeIndex >= 0) {
        resizeMarkup(markups[resizeIndex], point, source.width, source.height);
      } else if (active) {
        active.w = point.x - active.x;
        active.h = point.y - active.y;
      }
      redraw();
    }

    function onPointerUp(event: PointerEvent) {
      if (!dragMode) return;
      event.preventDefault();
      if (dragMode === "draw" && active) {
        const rect = normalizeCanvasRect(active);
        if (rect.w >= 8 && rect.h >= 8) {
          markups.push(createMarkupSnapshot(rect, markups.length, source.width, source.height));
          selectedIndex = markups.length - 1;
          renderMarkupList();
        }
      }
      active = null;
      dragMode = null;
      resizeIndex = -1;
      redraw();
    }

    function onPointerCancel() {
      active = null;
      dragMode = null;
      resizeIndex = -1;
      redraw();
    }

    function onUndo() {
      markups.pop();
      selectedIndex = Math.min(selectedIndex, markups.length - 1);
      renderMarkupList();
    }

    function onClear() {
      markups.splice(0, markups.length);
      selectedIndex = -1;
      renderMarkupList();
    }

    function onSkip() {
      finish({ markups: [] });
    }

    function onCancel() {
      finish(null);
    }

    function onConfirm() {
      active = null;
      finish({
        markups: markups.map((markup, index) => normalizeMarkupSnapshot(markup, index, source.width, source.height)),
      });
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(null);
      }
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        onConfirm();
      }
    }

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerCancel);
    undo.addEventListener("click", onUndo);
    clear.addEventListener("click", onClear);
    skip.addEventListener("click", onSkip);
    cancel.addEventListener("click", onCancel);
    confirm.addEventListener("click", onConfirm);
    document.addEventListener("keydown", onKeyDown, true);
    renderMarkupList();
  });
}

function drawMarkupRect(
  ctx: CanvasRenderingContext2D,
  rect: VisualMarkupRect | VisualMarkupSnapshot,
  active: boolean,
): void {
  const normalized = normalizeCanvasRect("bounds" in rect ? rect.bounds : rect);
  const color = "color" in rect ? rect.color : "#7dd3fc";
  const title = "title" in rect ? rect.title : "";
  ctx.save();
  ctx.lineWidth = active ? 5 : 4;
  ctx.strokeStyle = active ? "#ffffff" : color;
  ctx.fillStyle = `${color}24`;
  ctx.fillRect(normalized.x, normalized.y, normalized.w, normalized.h);
  ctx.strokeRect(normalized.x, normalized.y, normalized.w, normalized.h);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.strokeRect(normalized.x + 3, normalized.y + 3, Math.max(1, normalized.w - 6), Math.max(1, normalized.h - 6));
  if (title) {
    ctx.fillStyle = color;
    ctx.fillRect(normalized.x, Math.max(0, normalized.y - 22), Math.min(160, Math.max(44, title.length * 7 + 18)), 22);
    ctx.fillStyle = "#0f172a";
    ctx.font = "700 12px sans-serif";
    ctx.fillText(title, normalized.x + 7, Math.max(13, normalized.y - 7));
  }
  drawResizeHandle(ctx, normalized, color);
  ctx.restore();
}

function drawResizeHandle(ctx: CanvasRenderingContext2D, rect: VisualMarkupRect, color: string): void {
  ctx.fillStyle = "#0f172a";
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.fillRect(rect.x + rect.w - 7, rect.y + rect.h - 7, 14, 14);
  ctx.strokeRect(rect.x + rect.w - 7, rect.y + rect.h - 7, 14, 14);
}

function canvasPoint(canvas: HTMLCanvasElement, event: PointerEvent): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / Math.max(1, rect.width);
  const scaleY = canvas.height / Math.max(1, rect.height);
  return {
    x: Math.max(0, Math.min(canvas.width, Math.round((event.clientX - rect.left) * scaleX))),
    y: Math.max(0, Math.min(canvas.height, Math.round((event.clientY - rect.top) * scaleY))),
  };
}

function normalizeCanvasRect(rect: VisualMarkupRect): VisualMarkupRect {
  const x = rect.w < 0 ? rect.x + rect.w : rect.x;
  const y = rect.h < 0 ? rect.y + rect.h : rect.y;
  return {
    x: Math.round(x),
    y: Math.round(y),
    w: Math.round(Math.abs(rect.w)),
    h: Math.round(Math.abs(rect.h)),
  };
}

function createMarkupSnapshot(
  rect: VisualMarkupRect,
  index: number,
  canvasWidth: number,
  canvasHeight: number,
): VisualMarkupSnapshot {
  return normalizeMarkupSnapshot(
    {
      id: `markup-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      index: index + 1,
      color: VISUAL_MARKUP_COLORS[index % VISUAL_MARKUP_COLORS.length],
      title: `Cadre ${index + 1}`,
      note: null,
      bounds: rect,
    },
    index,
    canvasWidth,
    canvasHeight,
  );
}

function normalizeMarkupSnapshot(
  markup: VisualMarkupSnapshot,
  index: number,
  canvasWidth: number,
  canvasHeight: number,
): VisualMarkupSnapshot {
  const rect = clampMarkupRect(normalizeCanvasRect(markup.bounds), canvasWidth, canvasHeight);
  return {
    id: markup.id || `markup-${index + 1}`,
    index: index + 1,
    color: /^#[0-9a-f]{6}$/i.test(markup.color)
      ? markup.color
      : VISUAL_MARKUP_COLORS[index % VISUAL_MARKUP_COLORS.length],
    title: sanitizeText(markup.title || `Cadre ${index + 1}`, 80) || `Cadre ${index + 1}`,
    note: markup.note ? sanitizeText(markup.note, 240) : null,
    bounds: rect,
  };
}

function clampMarkupRect(rect: VisualMarkupRect, canvasWidth: number, canvasHeight: number): VisualMarkupRect {
  const x = Math.max(0, Math.min(canvasWidth - 1, rect.x));
  const y = Math.max(0, Math.min(canvasHeight - 1, rect.y));
  const w = Math.max(1, Math.min(canvasWidth - x, rect.w));
  const h = Math.max(1, Math.min(canvasHeight - y, rect.h));
  return { x, y, w, h };
}

function reindexMarkups(markups: VisualMarkupSnapshot[]): void {
  markups.forEach((markup, index) => {
    markup.index = index + 1;
    if (!markup.title || /^Cadre \d+$/.test(markup.title)) markup.title = `Cadre ${index + 1}`;
  });
}

function hitTestMarkup(markups: VisualMarkupSnapshot[], point: { x: number; y: number }): number {
  for (let index = markups.length - 1; index >= 0; index -= 1) {
    const rect = markups[index].bounds;
    if (point.x >= rect.x && point.x <= rect.x + rect.w && point.y >= rect.y && point.y <= rect.y + rect.h)
      return index;
  }
  return -1;
}

function hitTestResizeHandle(markups: VisualMarkupSnapshot[], point: { x: number; y: number }): number {
  for (let index = markups.length - 1; index >= 0; index -= 1) {
    const rect = markups[index].bounds;
    const hx = rect.x + rect.w;
    const hy = rect.y + rect.h;
    if (Math.abs(point.x - hx) <= 14 && Math.abs(point.y - hy) <= 14) return index;
  }
  return -1;
}

function resizeMarkup(
  markup: VisualMarkupSnapshot,
  point: { x: number; y: number },
  canvasWidth: number,
  canvasHeight: number,
): void {
  const x2 = Math.max(markup.bounds.x + 8, Math.min(canvasWidth, point.x));
  const y2 = Math.max(markup.bounds.y + 8, Math.min(canvasHeight, point.y));
  markup.bounds = {
    x: markup.bounds.x,
    y: markup.bounds.y,
    w: x2 - markup.bounds.x,
    h: y2 - markup.bounds.y,
  };
}

function imageDataUrlToCanvas(dataUrl: string): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(image, 0, 0);
      resolve(canvas);
    };
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

function encodeCanvas(canvas: HTMLCanvasElement): string {
  let smallest = "";
  for (const quality of [0.72, 0.58, 0.44]) {
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    if (dataUrl.length <= MAX_VISUAL_DATA_URL) return dataUrl;
    if (!smallest || dataUrl.length < smallest.length) smallest = dataUrl;
  }
  for (const target of [
    { width: 640, height: 460, quality: 0.42 },
    { width: 480, height: 340, quality: 0.38 },
    { width: 360, height: 260, quality: 0.34 },
    { width: 280, height: 200, quality: 0.3 },
    { width: 220, height: 160, quality: 0.26 },
    { width: 180, height: 128, quality: 0.22 },
    { width: 140, height: 100, quality: 0.2 },
    { width: 96, height: 72, quality: 0.18 },
  ]) {
    const reduced = document.createElement("canvas");
    const fit = Math.min(1, target.width / canvas.width, target.height / canvas.height);
    reduced.width = Math.max(1, Math.round(canvas.width * fit));
    reduced.height = Math.max(1, Math.round(canvas.height * fit));
    const ctx = reduced.getContext("2d");
    if (!ctx) continue;
    ctx.drawImage(canvas, 0, 0, reduced.width, reduced.height);
    const dataUrl = reduced.toDataURL("image/jpeg", target.quality);
    if (dataUrl.length <= MAX_VISUAL_DATA_URL) return dataUrl;
    if (!smallest || dataUrl.length < smallest.length) smallest = dataUrl;
  }
  return smallest || canvas.toDataURL("image/jpeg", 0.22);
}

function clampPoint(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(window.innerWidth, Math.round(x))),
    y: Math.max(0, Math.min(window.innerHeight, Math.round(y))),
  };
}

function normalizedBounds(a: { x: number; y: number }, b: { x: number; y: number }) {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}

function updateRegionBox(box: HTMLElement, a: { x: number; y: number }, b: { x: number; y: number }) {
  const bounds = normalizedBounds(a, b);
  box.style.display = "block";
  box.style.left = `${bounds.x}px`;
  box.style.top = `${bounds.y}px`;
  box.style.width = `${bounds.w}px`;
  box.style.height = `${bounds.h}px`;
}

function nextPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

function patchConsole(level: ConsoleLevel): void {
  const original = console[level].bind(console);
  console[level] = (...args: unknown[]) => {
    pushConsole({ t: Date.now(), level, message: sanitizeText(args.map((arg) => stringifyArg(arg)).join(" ")) });
    original(...args);
  };
}

function installWindowErrorCapture(): void {
  window.addEventListener("error", (event) => {
    const stack = event.error instanceof Error ? `\n${event.error.stack || ""}` : "";
    pushConsole({
      t: Date.now(),
      level: "error",
      message: sanitizeText(`${event.message || "window.error"} ${event.filename || ""}:${event.lineno || 0}${stack}`),
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    pushConsole({
      t: Date.now(),
      level: "error",
      message: sanitizeText(`unhandledrejection ${stringifyArg(event.reason)}`),
    });
  });
}

function patchFetch(): void {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = performance.now();
    const method = normalizeMethod(input, init);
    const url = normalizeUrl(input);
    try {
      const response = await originalFetch(input, init);
      if (!response.ok && shouldCaptureUrl(url)) {
        pushNetwork({
          t: Date.now(),
          kind: "fetch",
          method,
          url,
          status: response.status,
          ok: false,
          durationMs: Math.round(performance.now() - started),
        });
      }
      return response;
    } catch (err) {
      if (shouldCaptureUrl(url)) {
        pushNetwork({
          t: Date.now(),
          kind: "fetch",
          method,
          url,
          ok: false,
          durationMs: Math.round(performance.now() - started),
          error: sanitizeText(err instanceof Error ? err.message : String(err), 300),
        });
      }
      throw err;
    }
  };
}

function patchXhr(): void {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function patchedOpen(
    this: XMLHttpRequest & { __rwsBugReport?: { method: string; url: string; started: number } },
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    this.__rwsBugReport = { method: String(method || "GET").toUpperCase(), url: sanitizeUrl(url), started: 0 };
    return originalOpen.call(this, method, url, async ?? true, username ?? null, password ?? null);
  };

  XMLHttpRequest.prototype.send = function patchedSend(
    this: XMLHttpRequest & { __rwsBugReport?: { method: string; url: string; started: number } },
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    const meta = this.__rwsBugReport;
    if (meta) {
      meta.started = performance.now();
      const done = () => {
        if (!shouldCaptureUrl(meta.url) || (this.status >= 200 && this.status < 400)) return;
        pushNetwork({
          t: Date.now(),
          kind: "xhr",
          method: meta.method,
          url: meta.url,
          status: this.status,
          ok: false,
          durationMs: Math.round(performance.now() - meta.started),
        });
      };
      this.addEventListener("loadend", done, { once: true });
      this.addEventListener(
        "error",
        () => {
          if (!shouldCaptureUrl(meta.url)) return;
          pushNetwork({
            t: Date.now(),
            kind: "xhr",
            method: meta.method,
            url: meta.url,
            ok: false,
            durationMs: Math.round(performance.now() - meta.started),
            error: "network_error",
          });
        },
        { once: true },
      );
    }
    return originalSend.call(this, body);
  };
}

function pushConsole(entry: ConsoleCaptureEntry): void {
  if (isRecentConsoleDuplicate(entry)) return;
  consoleBuffer.push(entry);
  if (consoleBuffer.length > MAX_CONSOLE) consoleBuffer.splice(0, consoleBuffer.length - MAX_CONSOLE);
}

function pushNetwork(entry: NetworkCaptureEntry): void {
  networkBuffer.push(entry);
  if (networkBuffer.length > MAX_NETWORK) networkBuffer.splice(0, networkBuffer.length - MAX_NETWORK);
}

function pushWsMessage(entry: WebSocketCaptureEntry): void {
  if (isRecentWsDuplicate(entry)) return;
  wsBuffer.push(entry);
  if (wsBuffer.length > MAX_WS_MESSAGES) wsBuffer.splice(0, wsBuffer.length - MAX_WS_MESSAGES);
}

function latestConsoleError(): ConsoleCaptureEntry | null {
  for (let i = consoleBuffer.length - 1; i >= 0; i -= 1) {
    if (consoleBuffer[i].level === "error") return consoleBuffer[i];
  }
  return null;
}

function wsCaptureEntry(
  direction: WebSocketDirection,
  data: unknown,
  url?: string | URL | null,
): WebSocketCaptureEntry {
  const raw = wsDataToText(data);
  const parsed = parseJsonObject(raw);
  const sanitized = parsed ? sanitizeValue(parsed) : sanitizeText(raw, 900);
  const message = typeof sanitized === "string" ? sanitized : sanitizeText(JSON.stringify(sanitized), 900);
  const meta = parsed ? wsMessageMeta(parsed) : {};
  return {
    t: Date.now(),
    direction,
    kind: parsed ? "json" : "text",
    ...(url ? { url: sanitizeUrl(url) } : {}),
    ...meta,
    bytesApprox: wsDataBytes(data, raw),
    message,
  };
}

function wsDataToText(data: unknown): string {
  if (typeof data === "string") return data;
  return stringifyArg(data);
}

function wsDataBytes(data: unknown, raw: string): number {
  if (typeof data === "string") return data.length;
  return raw.length;
}

function parseJsonObject(raw: string): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function isBinaryWsData(data: unknown): boolean {
  return (
    data instanceof ArrayBuffer || ArrayBuffer.isView(data) || (typeof Blob !== "undefined" && data instanceof Blob)
  );
}

function isRecentConsoleDuplicate(entry: ConsoleCaptureEntry): boolean {
  for (let i = consoleBuffer.length - 1; i >= 0; i -= 1) {
    const prev = consoleBuffer[i];
    if (entry.t - prev.t > MESSAGE_DEDUPE_WINDOW_MS) return false;
    if (prev.level === entry.level && prev.message === entry.message) return true;
  }
  return false;
}

function isRecentWsDuplicate(entry: WebSocketCaptureEntry): boolean {
  for (let i = wsBuffer.length - 1; i >= 0; i -= 1) {
    const prev = wsBuffer[i];
    if (entry.t - prev.t > MESSAGE_DEDUPE_WINDOW_MS) return false;
    if (
      prev.direction === entry.direction &&
      prev.kind === entry.kind &&
      prev.type === entry.type &&
      prev.family === entry.family &&
      prev.op === entry.op &&
      prev.message === entry.message
    ) {
      return true;
    }
  }
  return false;
}

function wsMessageMeta(value: Record<string, unknown>): Pick<WebSocketCaptureEntry, "type" | "family" | "op"> {
  return {
    type: typeof value.type === "string" ? sanitizeText(value.type, 120) : null,
    family: typeof value.family === "string" ? sanitizeText(value.family, 120) : null,
    op: typeof value.op === "string" ? sanitizeText(value.op, 120) : null,
  };
}

function clickModifiers(event: MouseEvent): string[] {
  const out: string[] = [];
  if (event.altKey) out.push("Alt");
  if (event.ctrlKey) out.push("Ctrl");
  if (event.metaKey) out.push("Meta");
  if (event.shiftKey) out.push("Shift");
  return out;
}

function stringifyArg(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack || ""}`;
  if (typeof arg === "string") return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function stableJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function normalizeMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== "undefined" && input instanceof Request) return input.method.toUpperCase();
  return "GET";
}

function normalizeUrl(input: RequestInfo | URL): string {
  if (typeof Request !== "undefined" && input instanceof Request) return sanitizeUrl(input.url);
  return sanitizeUrl(input instanceof URL ? input : String(input));
}

function sanitizeUrl(input: string | URL): string {
  try {
    const url = new URL(String(input), window.location.origin);
    for (const key of Array.from(url.searchParams.keys())) {
      if (SENSITIVE_KEY.test(key)) url.searchParams.set(key, "[REDACTED]");
    }
    if (url.origin === window.location.origin) return `${url.pathname}${url.search}`;
    return `${url.origin}${url.pathname}${url.search}`;
  } catch {
    return sanitizeText(String(input), 300);
  }
}

function shouldCaptureUrl(url: string): boolean {
  return !url.includes("/api/bug-reports");
}

function currentBundleHash(): string | null {
  for (const script of Array.from(document.scripts)) {
    const match = script.src.match(/\/(?:console|operator)\.([A-Za-z0-9_-]+)\.js/);
    if (match) return match[1];
  }
  return null;
}

function activePanel(): string | null {
  const sessionTab = document.querySelector<HTMLElement>(".session-tab[data-active='true']");
  if (sessionTab?.textContent) return sanitizeText(sessionTab.textContent.replace("✕", "").trim(), 80);
  const navTab = document.querySelector<HTMLElement>(".console-nav-tab[data-active='true']");
  if (navTab?.textContent) return sanitizeText(navTab.textContent.trim(), 80);
  return null;
}

function findCurrentHostId(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get("hostId");
  if (fromUrl) return sanitizeText(fromUrl, 160);
  const el = document.querySelector<HTMLElement>("[data-host-id]");
  return el?.dataset.hostId ? sanitizeText(el.dataset.hostId, 160) : null;
}

function sanitizeWsState(state: Record<string, unknown>): Record<string, unknown> {
  return sanitizeValue({
    sessionCode: state.sessionCode,
    wsStatus: state.wsStatus,
    lastError: state.lastError,
    nativeAgentOnline: state.nativeAgentOnline,
    supportApproved: state.supportApproved,
    controlGranted: state.controlGranted,
    agentFeatures: state.agentFeatures,
    pluginVersions: state.pluginVersions,
    peers: Array.isArray(state.peers)
      ? state.peers.map((peer) => {
          const p = peer as Record<string, unknown>;
          return {
            role: p.role,
            active: p.active,
            platform: p.platform,
          };
        })
      : [],
  }) as Record<string, unknown>;
}

function createDomSnapshot(options: { annotations?: DomAnnotationSnapshot[] } = {}): DomNodeSnapshot | null {
  const annotations = options.annotations || [];
  const annotationMap = new Map<string, Array<{ id: string; index: number; title: string; note: string | null }>>();
  const elementAnnotationMap = new WeakMap<
    Element,
    Array<{ id: string; index: number; title: string; note: string | null }>
  >();
  for (const annotation of annotations) {
    const selector = annotation.selectedElement?.selector;
    if (!selector) continue;
    const list = annotationMap.get(selector) || [];
    const ref = {
      id: annotation.id,
      index: annotation.index,
      title: annotation.title,
      note: annotation.note || null,
    };
    list.push(ref);
    annotationMap.set(selector, list);
    const el = safeQuerySelector(selector);
    if (el) {
      const elementList = elementAnnotationMap.get(el) || [];
      elementList.push(ref);
      elementAnnotationMap.set(el, elementList);
    }
  }
  const root = findDomSnapshotRoot(annotations);
  return createDomSnapshotFrom(root, MAX_DOM_NODES, 5, { annotationMap, elementAnnotationMap });
}

function createDomSnapshotFrom(
  root: Element,
  maxNodes: number,
  maxDepth: number,
  options: DomSnapshotOptions = {},
): DomNodeSnapshot | null {
  let count = 0;
  function visit(el: Element, depth: number): DomNodeSnapshot | null {
    if (count >= maxNodes || depth > maxDepth) return null;
    if (el.matches(BUG_REPORT_UI_SELECTOR) || el.closest(BUG_REPORT_UI_SELECTOR)) return null;
    const htmlEl = el as HTMLElement;
    const style = window.getComputedStyle(htmlEl);
    if (style.display === "none" || style.visibility === "hidden") return null;
    count += 1;
    const rect = htmlEl.getBoundingClientRect();
    const masked = htmlEl.matches(PRIVATE_SELECTOR) || Boolean(htmlEl.closest(PRIVATE_SELECTOR));
    const selector = buildElementSelector(htmlEl);
    const classes = Array.from(htmlEl.classList)
      .filter((name) => !name.startsWith("bug-report-"))
      .slice(0, 5)
      .map((name) => sanitizeText(name, 60));
    const data = Object.fromEntries(Object.entries(targetDataset(htmlEl)).slice(0, 8));
    const attributes = Object.fromEntries(Object.entries(targetAttributes(htmlEl)).slice(0, 8));
    const label =
      sanitizeText(
        htmlEl.getAttribute("aria-label") ||
          htmlEl.getAttribute("title") ||
          htmlEl.getAttribute("data-testid") ||
          htmlEl.getAttribute("data-test") ||
          htmlEl.getAttribute("name") ||
          "",
        140,
      ) || null;
    const textPreview = domNodeTextPreview(htmlEl, masked);
    const annotationRefs = options.elementAnnotationMap?.get(el) || options.annotationMap?.get(selector);
    const node: DomNodeSnapshot = {
      tag: el.tagName.toLowerCase(),
      ...(htmlEl.id ? { id: sanitizeText(htmlEl.id, 120) } : {}),
      ...(classes.length ? { classes } : {}),
      selector,
      ...(htmlEl.getAttribute("role") ? { role: sanitizeText(htmlEl.getAttribute("role"), 60) } : {}),
      ...(label ? { label } : {}),
      ...(textPreview ? { textPreview } : {}),
      ...(Object.keys(data).length ? { data } : {}),
      ...(Object.keys(attributes).length ? { attributes } : {}),
      ...(masked ? { masked: true } : {}),
      ...(annotationRefs?.length ? { annotationRefs } : {}),
      bounds: {
        x: Math.round(rect.x),
        y: Math.round(rect.y),
        w: Math.round(rect.width),
        h: Math.round(rect.height),
      },
    };
    if (!masked && depth < maxDepth) {
      const childElements = Array.from(el.children);
      const children = childElements
        .map((child) => visit(child, depth + 1))
        .filter((child): child is DomNodeSnapshot => Boolean(child));
      if (children.length) node.children = children;
      const omitted = childElements.length - children.length;
      if (omitted > 0 && count >= maxNodes) node.truncatedChildren = omitted;
    } else if (!masked && el.children.length > 0) {
      node.truncatedChildren = el.children.length;
    }
    return node;
  }
  return visit(root, 0);
}

function findDomSnapshotRoot(annotations: DomAnnotationSnapshot[] = []): Element {
  const selectedElements = annotations
    .map((annotation) => safeQuerySelector(annotation.selectedElement.selector))
    .filter((el): el is HTMLElement => Boolean(el));
  const selectionRoot = sharedSnapshotRoot(selectedElements);
  if (selectionRoot) return selectionRoot;

  const modalRoot = visibleSnapshotCandidates(
    "dialog[open], [aria-modal='true'], [role='dialog'], .modal, .modal-content, .dialog, .drawer, .popover",
  )[0];
  if (modalRoot) return modalRoot;

  return (
    document.querySelector<HTMLElement>(".section-panel:not([style*='display: none'])") ||
    document.querySelector<HTMLElement>(".console-body") ||
    document.body
  );
}

function sharedSnapshotRoot(elements: HTMLElement[]): HTMLElement | null {
  if (!elements.length) return null;
  const rootSelector =
    "dialog[open], [aria-modal='true'], [role='dialog'], .modal, .modal-content, .dialog, .drawer, .popover, table, form, .section-panel, .console-body, main, section, aside";
  let current: HTMLElement | null = elements[0];
  while (current && current !== document.documentElement) {
    if (
      elements.every((item) => current?.contains(item)) &&
      (current.matches(rootSelector) || current === document.body)
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function visibleSnapshotCandidates(selector: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(selector))
    .filter((el) => {
      if (el.matches(BUG_REPORT_UI_SELECTOR) || el.closest(BUG_REPORT_UI_SELECTOR)) return false;
      const rect = el.getBoundingClientRect();
      if (rect.width < 20 || rect.height < 20) return false;
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden" && Number.parseFloat(style.opacity || "1") > 0;
    })
    .sort((a, b) => snapshotRootScore(b) - snapshotRootScore(a));
}

function snapshotRootScore(el: HTMLElement): number {
  const style = window.getComputedStyle(el);
  const zIndex = Number.parseInt(style.zIndex || "0", 10);
  const rect = el.getBoundingClientRect();
  return (Number.isFinite(zIndex) ? zIndex : 0) * 1_000_000 + Math.round(rect.width * rect.height);
}

function domNodeTextPreview(el: HTMLElement, masked: boolean): string | null {
  if (masked) return null;
  const directText = Array.from(el.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent || "")
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  const text = directText || (el.children.length === 0 ? (el.textContent || "").replace(/\s+/g, " ").trim() : "");
  return sanitizeText(text, 180) || null;
}

function pickElementAt(x: number, y: number): Element | null {
  let el = document.elementFromPoint(x, y);
  while (el && !isSelectableBugTarget(el)) el = el.parentElement;
  return el;
}

function isSelectableBugTarget(el: Element): boolean {
  if (el.matches(BUG_REPORT_UI_SELECTOR) || el.closest(BUG_REPORT_UI_SELECTOR)) return false;
  const tag = el.tagName.toLowerCase();
  if (["html", "head", "script", "style", "link", "meta"].includes(tag)) return false;
  const rect = el.getBoundingClientRect();
  return rect.width >= 2 && rect.height >= 2;
}

function updatePickFrame(target: Element | null, frame: HTMLElement, label: HTMLElement): void {
  if (!target) {
    frame.style.display = "none";
    label.style.display = "none";
    return;
  }
  placeFrame(frame, label, target.getBoundingClientRect(), describeElement(target as HTMLElement));
}

function placeFrame(
  frame: HTMLElement,
  label: HTMLElement,
  rect: Pick<DOMRect, "x" | "y" | "width" | "height">,
  text: string,
): void {
  frame.style.display = "block";
  frame.style.left = `${Math.round(rect.x)}px`;
  frame.style.top = `${Math.round(rect.y)}px`;
  frame.style.width = `${Math.round(rect.width)}px`;
  frame.style.height = `${Math.round(rect.height)}px`;
  label.style.display = "block";
  label.textContent = sanitizeText(text, 140);
  const top = rect.y > 28 ? rect.y - 28 : rect.y + rect.height + 6;
  label.style.left = `${Math.max(8, Math.round(rect.x))}px`;
  label.style.top = `${Math.max(8, Math.round(top))}px`;
}

function describeElement(el: HTMLElement): string {
  const table = targetTableContext(el);
  if (table) {
    const column = table.columnHeader || `colonne ${table.columnIndex}`;
    return sanitizeText(`${column}: ${table.cellText || table.rowText || "cellule"}`, 100);
  }
  const tag = el.tagName.toLowerCase();
  if (el.id) return `${tag}#${sanitizeText(el.id, 80)}`;
  const testId = el.getAttribute("data-testid") || el.getAttribute("data-test");
  if (testId) return `${tag}[${sanitizeText(testId, 80)}]`;
  const classes = Array.from(el.classList)
    .filter((name) => !name.startsWith("bug-report-"))
    .slice(0, 2);
  return classes.length ? `${tag}.${classes.map((name) => sanitizeText(name, 40)).join(".")}` : tag;
}

function targetDataset(el: HTMLElement): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(el.dataset).slice(0, 20)) {
    out[key] = SENSITIVE_KEY.test(key) ? "[REDACTED]" : sanitizeText(value || "", 220);
  }
  return out;
}

function targetAttributes(el: HTMLElement): Record<string, string> {
  const out: Record<string, string> = {};
  for (const attr of SAFE_TARGET_ATTRIBUTES) {
    const value = el.getAttribute(attr);
    if (value === null) continue;
    out[attr] = SENSITIVE_KEY.test(attr)
      ? "[REDACTED]"
      : sanitizeText(attr === "href" ? sanitizeUrl(value) : value, 260);
  }
  return out;
}

function targetLabels(
  el: HTMLElement,
  table: ElementTableContext | null,
  component: ElementComponentHint | null,
): string[] {
  const labels = new Set<string>();
  const add = (value: unknown, max = 160) => {
    const text = sanitizeText(value, max).replace(/\s+/g, " ").trim();
    if (text) labels.add(text);
  };
  add(el.getAttribute("aria-label"));
  add(el.getAttribute("title"));
  add(el.getAttribute("name"));
  add(el.getAttribute("data-testid") || el.getAttribute("data-test"));
  const labelledBy = el.getAttribute("aria-labelledby");
  if (labelledBy) {
    for (const id of labelledBy.split(/\s+/)) add(document.getElementById(id)?.textContent, 120);
  }
  const closestLabel = el.closest("label");
  if (closestLabel) add(closestLabel.textContent, 180);
  if (table?.columnHeader) add(`Colonne: ${table.columnHeader}`, 120);
  if (table?.cellText) add(`Cellule: ${table.cellText}`, 180);
  if (component?.name) add(`Composant: ${component.name}`, 120);
  return Array.from(labels).slice(0, 10);
}

function targetTableContext(el: HTMLElement): ElementTableContext | null {
  const cell = el.closest<HTMLTableCellElement>("td,th");
  const row = cell?.closest<HTMLTableRowElement>("tr");
  const table = row?.closest<HTMLTableElement>("table");
  if (!cell || !row || !table) return null;

  const headers = tableHeaders(table);
  const cells = Array.from(row.cells);
  const rowCells = cells.map((item) => sanitizeText(item.textContent?.replace(/\s+/g, " ").trim() || "", 240));
  const rowMap: Record<string, string> = {};
  for (const [index, value] of rowCells.entries()) {
    rowMap[headers[index] || `col_${index + 1}`] = value;
  }

  const bodyRows = Array.from(table.tBodies).flatMap((body) => Array.from(body.rows));
  const rows = bodyRows.length ? bodyRows : Array.from(table.rows).filter((item) => item.cells.length > 0);
  const rowIndex = Math.max(0, rows.indexOf(row)) + 1;
  const columnIndex = cell.cellIndex + 1;
  const tableLabel =
    sanitizeText(
      table.caption?.textContent ||
        table.getAttribute("aria-label") ||
        table.closest<HTMLElement>("[aria-label]")?.getAttribute("aria-label") ||
        "",
      160,
    ) || null;

  return {
    tableLabel,
    rowIndex,
    columnIndex,
    columnHeader: headers[cell.cellIndex] || null,
    cellText: rowCells[cell.cellIndex] || null,
    rowText: sanitizeText(rowCells.filter(Boolean).join(" | "), 600) || null,
    headers: headers.slice(0, 20),
    row: rowMap,
  };
}

function tableHeaders(table: HTMLTableElement): string[] {
  const headCells = table.tHead ? Array.from(table.tHead.querySelectorAll("th")) : [];
  const firstHeaderRow = headCells.length ? headCells : Array.from(table.querySelectorAll("tr:first-child th"));
  return firstHeaderRow.map((cell, index) => {
    const text = sanitizeText(cell.textContent?.replace(/\s+/g, " ").trim() || "", 120);
    return text || `col_${index + 1}`;
  });
}

function inferComponent(el: HTMLElement, panel: string | null): ElementComponentHint | null {
  const componentEl = el.closest<HTMLElement>(COMPONENT_HINT_SELECTOR);
  if (!componentEl) return panel ? { name: panel, source: "active-panel", selector: null } : null;
  const explicit =
    componentEl.getAttribute("data-component") ||
    componentEl.getAttribute("data-testid") ||
    componentEl.getAttribute("data-test") ||
    componentEl.getAttribute("aria-label") ||
    componentEl.getAttribute("role") ||
    componentEl.tagName.toLowerCase();
  const tableLabel = componentEl instanceof HTMLTableElement ? targetTableName(componentEl) : "";
  const name = sanitizeText(tableLabel || explicit || panel || componentEl.tagName.toLowerCase(), 160);
  return {
    name: panel && componentEl.tagName.toLowerCase() === "table" ? `${panel} / ${name}` : name,
    source: componentSource(componentEl),
    selector: buildElementSelector(componentEl),
  };
}

function targetTableName(table: HTMLTableElement): string {
  const caption = table.caption?.textContent?.replace(/\s+/g, " ").trim();
  if (caption) return caption;
  const headers = tableHeaders(table).filter(Boolean).slice(0, 3).join(", ");
  return headers ? `table (${headers})` : "table";
}

function componentSource(el: HTMLElement): string {
  if (el.hasAttribute("data-component")) return "data-component";
  if (el.hasAttribute("data-testid")) return "data-testid";
  if (el.hasAttribute("data-test")) return "data-test";
  if (el.hasAttribute("aria-label")) return "aria-label";
  if (el.hasAttribute("role")) return "role";
  return el.tagName.toLowerCase();
}

function targetAncestry(el: HTMLElement): ElementAncestorSnapshot[] {
  const out: ElementAncestorSnapshot[] = [];
  let current: HTMLElement | null = el;
  while (current && current !== document.body && out.length < 7) {
    const masked = current.matches(PRIVATE_SELECTOR) || Boolean(current.closest(PRIVATE_SELECTOR));
    out.push({
      tag: current.tagName.toLowerCase(),
      id: current.id ? sanitizeText(current.id, 120) : null,
      role: current.getAttribute("role") ? sanitizeText(current.getAttribute("role"), 80) : null,
      classes: Array.from(current.classList)
        .filter((name) => !name.startsWith("bug-report-"))
        .slice(0, 5)
        .map((name) => sanitizeText(name, 60)),
      selector: buildElementSelector(current),
      label:
        sanitizeText(
          current.getAttribute("aria-label") ||
            current.getAttribute("title") ||
            current.getAttribute("data-testid") ||
            current.getAttribute("data-test") ||
            "",
          140,
        ) || null,
      textPreview: masked ? null : sanitizeText((current.textContent || "").replace(/\s+/g, " ").trim(), 220) || null,
    });
    current = current.parentElement;
  }
  return out;
}

function targetDebugSummary(
  el: HTMLElement,
  panel: string | null,
  component: ElementComponentHint | null,
  table: ElementTableContext | null,
  textPreview: string | null,
): string {
  if (table) {
    const column = table.columnHeader ? `colonne "${table.columnHeader}"` : `colonne ${table.columnIndex}`;
    const value = table.cellText ? ` = "${sanitizeText(table.cellText, 80)}"` : "";
    return sanitizeText(`${panel || component?.name || "Table"}: ligne ${table.rowIndex}, ${column}${value}`, 220);
  }
  if (el.id) return `${el.tagName.toLowerCase()}#${sanitizeText(el.id, 120)}`;
  const testId = el.getAttribute("data-testid") || el.getAttribute("data-test");
  if (testId) return `${el.tagName.toLowerCase()}[${sanitizeText(testId, 120)}]`;
  if (textPreview)
    return sanitizeText(`${component?.name || panel || el.tagName.toLowerCase()}: "${textPreview}"`, 220);
  return sanitizeText(`${component?.name || panel || "DOM"}: ${buildElementSelector(el)}`, 220);
}

function buildElementSelector(el: HTMLElement): string {
  if (el.id) return `${el.tagName.toLowerCase()}#${cssEscape(el.id)}`;
  const path: string[] = [];
  let current: Element | null = el;
  while (current && current !== document.body && path.length < 6) {
    const part = selectorPart(current as HTMLElement);
    path.unshift(part);
    const selector = path.join(" > ");
    try {
      if (document.querySelectorAll(selector).length === 1) return selector;
    } catch {
      // Keep building a more conservative path.
    }
    current = current.parentElement;
  }
  return path.join(" > ") || el.tagName.toLowerCase();
}

function selectorPart(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const testIdAttr = el.hasAttribute("data-testid") ? "data-testid" : el.hasAttribute("data-test") ? "data-test" : null;
  if (testIdAttr) return `${tag}[${testIdAttr}="${cssString(el.getAttribute(testIdAttr) || "")}"]`;
  const classes = Array.from(el.classList)
    .filter((name) => !name.startsWith("bug-report-") && /^[A-Za-z_-][\w-]*$/.test(name))
    .slice(0, 2);
  let part = `${tag}${classes.map((name) => `.${cssEscape(name)}`).join("")}`;
  const parent = el.parentElement;
  if (!parent) return part;
  const sameTag = Array.from(parent.children).filter((child) => child.tagName === el.tagName);
  if (sameTag.length > 1) {
    part += `:nth-of-type(${sameTag.indexOf(el) + 1})`;
  }
  return part;
}

function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return value.replace(/["\\]/g, "\\$&");
}

function cssString(value: string): string {
  return value.replace(/["\\]/g, "\\$&").replace(/\n/g, "\\A ").replace(/\r/g, "\\D ").replace(/\f/g, "\\C ");
}

function safeQuerySelector(selector: string): HTMLElement | null {
  try {
    return document.querySelector<HTMLElement>(selector);
  } catch {
    return null;
  }
}
